/**
 * Slide-out drawer listing the patient's other encounters so a clinician
 * can review past visits without leaving the current note. Rows open in
 * a new browser tab so the current encounter's autosave state isn't lost.
 */
import { useEffect, useState } from 'react';
import {
    Box,
    Drawer,
    Typography,
    Stack,
    Divider,
    Chip,
    IconButton,
    CircularProgress,
    Tooltip,
} from '@mui/material';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import EventNoteRoundedIcon from '@mui/icons-material/EventNoteRounded';
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded';
import { useSelector } from 'react-redux';
import { listEncountersByPatient } from '../../../api/encounter';

const NAVY = '#052F5D';

function formatDate(d) {
    if (!d) return '—';
    const date = new Date(d);
    if (Number.isNaN(date.getTime())) return '—';
    return date.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

export default function PriorEncountersDrawer({ open, onClose, patientId, currentEncounterId }) {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const { userData } = useSelector((s) => s.auth);
    const role = userData?.role || 'admin';

    useEffect(() => {
        if (!open || !patientId) return;
        let cancelled = false;
        (async () => {
            setLoading(true);
            try {
                const res = await listEncountersByPatient(patientId);
                if (!cancelled) setItems(res.data || []);
            } catch {
                if (!cancelled) setItems([]);
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [open, patientId]);

    const others = items.filter((e) => e._id !== currentEncounterId);

    return (
        <Drawer
            anchor="right"
            open={open}
            onClose={onClose}
            PaperProps={{ sx: { width: { xs: '100%', sm: 420 } } }}
        >
            <Box sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                    <Typography variant="overline" sx={{ color: 'text.secondary', letterSpacing: 1.2, fontWeight: 700, fontSize: 10 }}>
                        Encounter history
                    </Typography>
                    <Typography sx={{ fontWeight: 700, color: NAVY, fontSize: '1.0625rem' }}>
                        Prior encounters
                    </Typography>
                </Box>
                <IconButton onClick={onClose} size="small"><CloseRoundedIcon /></IconButton>
            </Box>
            <Divider />

            {loading ? (
                <Box sx={{ p: 4, textAlign: 'center' }}>
                    <CircularProgress size={22} sx={{ color: NAVY }} />
                </Box>
            ) : others.length === 0 ? (
                <Box sx={{ p: 4, textAlign: 'center' }}>
                    <EventNoteRoundedIcon sx={{ fontSize: 32, color: NAVY, opacity: 0.4 }} />
                    <Typography sx={{ mt: 1, color: 'text.secondary', fontSize: 14 }}>
                        No other encounters on file.
                    </Typography>
                </Box>
            ) : (
                <Stack divider={<Divider flexItem />}>
                    {others.map((e) => {
                        const isFin = e.status === 'finalized';
                        // A signed visit reopened for correction reads as
                        // "Amending" here too, so the chart doesn't claim a
                        // record is locked while someone is editing it.
                        const isAmending = (e.amendments || []).some((a) => !a.closedAt);
                        const primary = (e.diagnoses || []).find((d) => d.primary) || (e.diagnoses || [])[0];
                        const href = `/${role}/patients/${patientId}/encounters/${e._id}`;
                        return (
                            <Box
                                key={e._id}
                                component="a"
                                href={href}
                                target="_blank"
                                rel="noopener"
                                sx={{
                                    display: 'block',
                                    px: 2,
                                    py: 1.5,
                                    textDecoration: 'none',
                                    color: 'inherit',
                                    transition: 'background-color 140ms ease',
                                    '&:hover': { bgcolor: 'rgba(5,47,93,0.04)' },
                                }}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                    <Typography sx={{ fontWeight: 600, color: NAVY, fontSize: 13.5 }}>
                                        {formatDate(e.visitDate)}
                                    </Typography>
                                    <Chip
                                        size="small"
                                        icon={isFin && !isAmending ? <LockOutlinedIcon sx={{ fontSize: 12 }} /> : <EditOutlinedIcon sx={{ fontSize: 12 }} />}
                                        label={isAmending ? 'Amending' : isFin ? 'Finalized' : 'Draft'}
                                        sx={{
                                            height: 20,
                                            fontSize: 11,
                                            bgcolor: isAmending ? '#FFF6E5' : isFin ? 'rgba(46,125,50,0.10)' : 'rgba(5,47,93,0.08)',
                                            color: isAmending ? '#5C3A00' : isFin ? 'success.dark' : NAVY,
                                            border: '1px solid',
                                            borderColor: isAmending ? '#F5C26B' : isFin ? 'rgba(46,125,50,0.25)' : 'rgba(5,47,93,0.15)',
                                            fontWeight: 600,
                                            '.MuiChip-icon': { color: 'inherit' },
                                        }}
                                    />
                                    <Tooltip title="Opens in new tab" arrow>
                                        <OpenInNewRoundedIcon sx={{ fontSize: 14, color: 'text.disabled', ml: 'auto' }} />
                                    </Tooltip>
                                </Box>
                                <Typography variant="body2" sx={{ color: 'text.primary' }} noWrap>
                                    {e.chiefComplaint || (
                                        <Box component="span" sx={{ color: 'text.disabled', fontStyle: 'italic' }}>
                                            No chief complaint
                                        </Box>
                                    )}
                                </Typography>
                                <Typography variant="caption" noWrap sx={{ color: 'text.secondary', display: 'block' }}>
                                    {primary
                                        ? <>Dx: {(primary.code || primary.icd10Code) ? <><strong>{primary.code || primary.icd10Code}</strong>{' '}</> : null}{primary.description}</>
                                        : 'No diagnosis recorded'}
                                </Typography>
                            </Box>
                        );
                    })}
                </Stack>
            )}
        </Drawer>
    );
}
